import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueries } from '@tanstack/react-query'
import { Badge, Button, Spinner, ConfirmDialog } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import type { Notification } from '@/types'
import { formatDistanceToNow, format, parseISO } from 'date-fns'
import { cn } from '@/lib/cn'
import { ExternalLink } from 'lucide-react'
import { useMyCompanies } from '@/features/companies/hooks/useCompanies'
import {
  useShiftAssignment,
  useConfirmShiftAssignment,
  useAcknowledgeShiftAlert,
  shiftAssignmentsQueryKeys,
} from '@/features/shifts/hooks/useShifts'
import {
  resolveShiftAlertContext,
  getShiftAssignmentById,
} from '@/api/services/shifts.service'
import {
  extractShiftAlertContext,
  assignmentIdFromShiftsPath,
  companyIdFromShiftsPath,
  getNotificationActionStatus,
  isConfirmShiftActionAvailable,
  isReadOnlyShiftAction,
} from '../utils/shiftAlertNotificationContext'
import { toAccessiblePath } from '../utils/notificationNavigation'
import { usePermissions } from '@/hooks/usePermissions'

export interface ShiftAlertNotificationRowProps {
  notification: Notification
  onMarkAsRead: (id: string, silent?: boolean) => void
}

function formatTime(iso: string): string {
  try {
    const date = parseISO(iso)
    const now = new Date()
    const diffDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    if (diffDays < 1) return formatDistanceToNow(date, { addSuffix: true })
    return format(date, 'PPp')
  } catch {
    return iso
  }
}

function priorityBadgeVariant(priority: string): BadgeVariant {
  switch (priority) {
    case 'urgent':
      return 'danger'
    case 'high':
      return 'warning'
    case 'low':
      return 'neutral'
    default:
      return 'neutral'
  }
}

export function ShiftAlertNotificationRow({
  notification: n,
  onMarkAsRead,
}: ShiftAlertNotificationRowProps): React.ReactElement {
  const { can } = usePermissions()
  const [reminderStoppedLocal, setReminderStoppedLocal] = useState(false)
  const [showAckDialog, setShowAckDialog] = useState(false)
  const { data: myCompanies = [] } = useMyCompanies()
  const ctx = useMemo(() => extractShiftAlertContext(n), [n])

  const companyIdsForResolve = useMemo(() => {
    if (ctx.companyId) return [ctx.companyId]
    return myCompanies.map((c) => c._id)
  }, [ctx.companyId, myCompanies])

  const assignmentFromLink =
    ctx.shiftsPlanningPath != null
      ? assignmentIdFromShiftsPath(ctx.shiftsPlanningPath)
      : undefined
  const companyFromLink =
    ctx.shiftsPlanningPath != null
      ? companyIdFromShiftsPath(ctx.shiftsPlanningPath)
      : undefined

  const needsResolve =
    !!ctx.alertId &&
    !ctx.shiftAssignmentId &&
    !assignmentFromLink &&
    companyIdsForResolve.length > 0

  const sortedKey = [...companyIdsForResolve].sort().join(',')

  const {
    data: resolved,
    isLoading: resolveLoading,
    isError: resolveError,
  } = useQuery({
    queryKey: ['shift-alert-resolve', ctx.alertId ?? '', sortedKey],
    queryFn: () => resolveShiftAlertContext(ctx.alertId!, companyIdsForResolve),
    enabled: needsResolve && !!ctx.alertId,
    staleTime: 5 * 60 * 1000,
  })

  const assignmentFromCtx =
    ctx.shiftAssignmentId ?? resolved?.shiftAssignmentId ?? assignmentFromLink

  const baseCompanyId =
    ctx.companyId ??
    resolved?.companyId ??
    companyFromLink ??
    (myCompanies.length === 1 ? myCompanies[0]._id : undefined)

  const shouldProbeCompany =
    !!ctx.shiftAssignmentId &&
    !baseCompanyId &&
    myCompanies.length > 1

  const probeQueries = useQueries({
    queries: myCompanies.map((c) => ({
      queryKey: shiftAssignmentsQueryKeys.detail(c._id, ctx.shiftAssignmentId ?? ''),
      queryFn: () => getShiftAssignmentById(c._id, ctx.shiftAssignmentId!),
      enabled: shouldProbeCompany && !!ctx.shiftAssignmentId,
      retry: false,
    })),
  })

  const probedCompanyId = useMemo(() => {
    if (!shouldProbeCompany || !ctx.shiftAssignmentId) return undefined
    for (let i = 0; i < probeQueries.length; i++) {
      const q = probeQueries[i]
      if (q.data?._id) return myCompanies[i]._id
    }
    return undefined
  }, [shouldProbeCompany, ctx.shiftAssignmentId, probeQueries, myCompanies])

  const probeLoading = shouldProbeCompany && probeQueries.some((q) => q.isPending)

  const effectiveCompanyId = baseCompanyId ?? probedCompanyId
  const effectiveAssignmentId = assignmentFromCtx

  const { data: assignment, isLoading: assignmentLoading, isError: assignmentError } =
    useShiftAssignment(effectiveCompanyId ?? null, effectiveAssignmentId ?? null)

  const confirmShift = useConfirmShiftAssignment()
  const acknowledgeAlert = useAcknowledgeShiftAlert()

  const actionStatus = getNotificationActionStatus(n)
  const readOnlyShiftAction = isReadOnlyShiftAction(n)
  const confirmActionAvailable = isConfirmShiftActionAvailable(n)
  const assignmentStatus = assignment?.status
  const canConfirmShift =
    confirmActionAvailable &&
    !!effectiveCompanyId &&
    !!effectiveAssignmentId &&
    !readOnlyShiftAction &&
    !assignmentError &&
    (assignmentLoading || assignment?.status === 'scheduled')

  const showConfirmSpinner =
    resolveLoading ||
    probeLoading ||
    (assignmentLoading && !!effectiveAssignmentId && !!effectiveCompanyId)

  const handleConfirm = (): void => {
    if (!effectiveCompanyId || !effectiveAssignmentId) return
    confirmShift.mutate(
      { companyId: effectiveCompanyId, id: effectiveAssignmentId },
      {
        onSuccess: () => onMarkAsRead(n._id, true),
      }
    )
  }

  const handleAcknowledge = (): void => {
    if (!ctx.alertId || !effectiveCompanyId) return
    acknowledgeAlert.mutate(
      { companyId: effectiveCompanyId, alertId: ctx.alertId },
      {
        onSuccess: () => {
          setReminderStoppedLocal(true)
          setShowAckDialog(false)
        },
      }
    )
  }

  const rawPlanningLink =
    ctx.shiftsPlanningPath != null && ctx.shiftsPlanningPath.startsWith('/')
      ? ctx.shiftsPlanningPath
      : effectiveCompanyId
        ? `/companies/${effectiveCompanyId}/shifts${effectiveAssignmentId ? `?assignment=${encodeURIComponent(effectiveAssignmentId)}` : ''}`
        : myCompanies[0]
          ? `/companies/${myCompanies[0]._id}/shifts`
          : undefined
  // Degrade to self-service shifts when the user can't reach team planning (same global layer
  // the notification list/dropdown use).
  const planningLink = rawPlanningLink ? toAccessiblePath(rawPlanningLink, can) : undefined
  const planningLinkLabel = planningLink === '/my-shifts' ? 'My shifts' : 'Shift planning'

  let statusHint: string | null = null
  if (actionStatus === 'expired') {
    statusHint = 'This shift has expired and can only be viewed.'
  } else if (actionStatus === 'confirmed' || assignmentStatus === 'confirmed') {
    statusHint = 'This shift is already confirmed.'
  } else if (actionStatus === 'cancelled' || assignmentStatus === 'cancelled') {
    statusHint = 'This shift assignment was cancelled.'
  } else if (assignmentError && effectiveAssignmentId && effectiveCompanyId) {
    statusHint = 'Could not load this assignment. Open Shift planning for the correct company.'
  } else if (resolveError && needsResolve) {
    statusHint =
      'Could not load shift alert details. Use Shift planning or ask your admin to include company context on notifications.'
  } else if (reminderStoppedLocal) {
    statusHint = 'Reminder dismissed — confirm your shift in Shift planning if you have not yet.'
  }

  return (
    <>
      <tr
        className={cn(
          'text-sm transition-colors',
          n.isRead
            ? 'text-gray-600 dark:text-gray-400'
            : 'bg-primary/5 font-medium text-gray-900 dark:bg-primary/10 dark:text-gray-100'
        )}
      >
        <td colSpan={4} className="px-4 py-4 align-top">
          <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-900/40">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{n.title}</span>
                  {!n.isRead && (
                    <Badge variant="info">Action needed</Badge>
                  )}
                  <Badge variant={priorityBadgeVariant(n.priority)}>
                    {n.type}
                  </Badge>
                  {readOnlyShiftAction && (
                    <Badge variant="neutral">
                      {actionStatus === 'expired'
                        ? 'Expired'
                        : actionStatus === 'confirmed'
                          ? 'Confirmed'
                          : 'Cancelled'}
                    </Badge>
                  )}
                </div>
                {n.message && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{n.message}</p>
                )}
                {statusHint && (
                  <p className="text-sm text-amber-800 dark:text-amber-200/90">{statusHint}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {n.createdAt ? formatTime(n.createdAt) : '—'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {showConfirmSpinner && (
                <Spinner size="sm" className="text-primary" aria-label="Loading shift details" />
              )}
              {canConfirmShift && assignmentStatus !== 'confirmed' && assignmentStatus !== 'cancelled' && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={showConfirmSpinner} loading={confirmShift.isPending}
                >
                  <span className={confirmShift.isPending ? 'ml-2' : ''}>Confirm shift</span>
                </Button>
              )}
              {ctx.alertId && effectiveCompanyId && !reminderStoppedLocal && !readOnlyShiftAction && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAckDialog(true)}
                  loading={acknowledgeAlert.isPending}
                >
                  Stop reminders
                </Button>
              )}
              {ctx.alertId && !effectiveCompanyId && myCompanies.length > 1 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Open Shift planning from the correct company to use reminder actions.
                </span>
              )}
              {planningLink && (
                <Link
                  to={planningLink}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  onClick={() => {
                    if (!n.isRead) onMarkAsRead(n._id, true)
                  }}
                >
                  {planningLinkLabel}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </Link>
              )}
              {n.reference?.url && !planningLink && (() => {
                const url = n.reference!.url!
                let internalPath: string | undefined
                try {
                  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
                  const u = new URL(url, base)
                  if (typeof window === 'undefined' || u.origin === window.location.origin) {
                    internalPath = `${u.pathname}${u.search}${u.hash}`
                  }
                } catch {
                  /* ignore */
                }
                if (internalPath) {
                  return (
                    <Link
                      to={internalPath}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                      onClick={() => {
                        if (!n.isRead) onMarkAsRead(n._id, true)
                      }}
                    >
                      View
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  )
                }
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    onClick={() => {
                      if (!n.isRead) onMarkAsRead(n._id, true)
                    }}
                  >
                    View
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  </a>
                )
              })()}
              {!n.isRead && (
                <Button type="button" variant="ghost" onClick={() => onMarkAsRead(n._id)}>
                  Mark read
                </Button>
              )}
            </div>
          </div>
        </td>
      </tr>
      <ConfirmDialog
        open={showAckDialog}
        onClose={() => setShowAckDialog(false)}
        onConfirm={handleAcknowledge}
        title="Stop reminders?"
        message="This stops further shift reminders for this alert. It does not mark you as available for the shift. Use Confirm shift for that."
        confirmLabel="Stop reminders"
        confirmLoading={acknowledgeAlert.isPending}
      />
    </>
  )
}

ShiftAlertNotificationRow.displayName = 'ShiftAlertNotificationRow'
